<div class="inventory-panel flex flex-column gap-4">
  @if (loading() && !inventory()) {
    <div class="flex align-items-center gap-2 text-500 p-4">
      <i class="fas fa-spinner fa-spin"></i> Übersicht wird geladen …
    </div>
  }

  @if (error(); as message) {
    <div class="p-3 border-round surface-100 text-red-600">
      <i class="fas fa-triangle-exclamation mr-2"></i>{{ message }}
    </div>
  }

  @if (inventory(); as inv) {
    <!-- Head: status + actions -->
    <section class="flex flex-column md:flex-row md:align-items-center gap-3">
      <div class="flex-1">
        <app-header
          icon="fas fa-note-sticky"
          [text]="inv.bookmark.title"
          sub="Alles, was diese Ansicht nutzt – und was für die Veröffentlichung noch fehlt" />
        <div class="flex flex-wrap align-items-center gap-2 mt-2">
          <p-tag
            [value]="audienceLabel(inv.bookmark.audience)"
            [severity]="
              inv.bookmark.audience === 'public' ? 'info' : 'secondary'
            "
            [icon]="
              inv.bookmark.audience === 'public'
                ? 'fas fa-earth-europe'
                : 'fas fa-house-user'
            " />
          @if (inv.readiness.readyForPublic) {
            <p-tag
              value="bereit für die Veröffentlichung"
              severity="success"
              icon="fas fa-check" />
          } @else {
            <p-tag
              [value]="
                inv.readiness.blockingCount +
                (inv.readiness.blockingCount === 1
                  ? ' Punkt fehlt'
                  : ' Punkte fehlen')
              "
              severity="warning"
              icon="fas fa-triangle-exclamation" />
          }
        </div>
      </div>

      <div class="flex flex-wrap gap-2 md:justify-content-end">
        @if (canPublish() && toPublishCount() > 0) {
          <p-button
            icon="fas fa-earth-europe"
            [label]="'Fehlendes freigeben (' + toPublishCount() + ')'"
            [loading]="publishing()"
            [rounded]="true"
            severity="success"
            pTooltip="Veröffentlicht alle Jahre, Daten, Themen und Berichte, die diese Ansicht noch braucht"
            tooltipPosition="bottom"
            (click)="publishMissing()" />
        }
        @if (
          canPublish() &&
          inv.readiness.readyForPublic &&
          !inv.bookmark.published &&
          !inv.bookmark.private
        ) {
          <p-button
            icon="fas fa-check-circle"
            label="Ansicht veröffentlichen"
            [rounded]="true"
            [outlined]="true"
            (click)="publishView()" />
        }
        <p-button
          icon="fas fa-rotate"
          [rounded]="true"
          [text]="true"
          pTooltip="Neu laden"
          tooltipPosition="bottom"
          [loading]="loading()"
          (click)="load(inv.bookmark._id)" />
      </div>
    </section>

    @if (inv.readiness.unfixable.length > 0) {
      <div class="p-3 border-round surface-100">
        <div class="font-semibold mb-2">
          <i class="fas fa-screwdriver-wrench mr-2 text-red-500"></i>
          Nicht per Freigabe lösbar ({{ inv.readiness.unfixable.length }})
        </div>
        <ul class="m-0 pl-4 text-sm">
          @for (issue of inv.readiness.unfixable; track $index) {
            <li>
              <b>{{ issueWidgetLabel(issue.widgetId) }}</b
              >: {{ issue.message }}
            </li>
          }
        </ul>
        <div class="text-sm text-500 mt-2">
          Gelöschte oder fehlende Verweise lassen sich im Validierungs-Panel aus
          den Widgets entfernen.
        </div>
      </div>
    }

    <!-- Key figures -->
    <section class="grid">
      <div class="col-6 lg:col-3">
        <app-stat-card
          icon="fas fa-table-cells-large"
          label="Widgets"
          [value]="inv.widgets.length" />
      </div>
      <div class="col-6 lg:col-3">
        <app-stat-card
          icon="fas fa-ruler-vertical"
          color="cyan"
          label="Indikatoren"
          [value]="inv.indicators.length" />
      </div>
      <div class="col-6 lg:col-3">
        <app-stat-card
          icon="fas fa-map-location-dot"
          color="green"
          label="Räume & Standorte"
          [value]="inv.spaces.length" />
      </div>
      <div class="col-6 lg:col-3">
        <app-stat-card
          icon="fas fa-earth-europe"
          color="orange"
          label="noch freizugeben"
          [value]="toPublishCount()" />
      </div>
    </section>

    <!-- Filter -->
    <section
      class="inventory-filter flex flex-wrap align-items-center gap-3 p-2 border-round"
      [class.active]="!!selectedWidget()">
      <!-- not wrapped in a <label>: the label's forwarded click would toggle
           the switch right back -->
      <div class="flex align-items-center gap-2">
        <p-inputSwitch
          inputId="inventory-only-problems"
          [ngModel]="onlyProblems()"
          (ngModelChange)="onlyProblems.set($event)" />
        <label for="inventory-only-problems" class="cursor-pointer">
          nur Nicht-Öffentliches &amp; Probleme
        </label>
      </div>
      @if (selectedWidget(); as widget) {
        <span class="flex-1 flex align-items-center gap-2">
          <i class="fas fa-filter"></i>
          <span>
            Nur, was <b>#{{ widget.position }} „{{ widgetLabel(widget) }}“</b>
            nutzt
            @if (view(); as v) {
              <span class="text-sm">
                · {{ v.indicators.length }} Indikatoren ·
                {{ v.data.length }} Daten</span
              >
            }
          </span>
        </span>
        <p-button
          label="Alle anzeigen"
          icon="fas fa-xmark"
          size="small"
          [rounded]="true"
          (click)="selectedWidgetId.set(null)" />
      } @else {
        <span class="text-sm text-500">
          <i class="fas fa-hand-pointer mr-1"></i>
          Klick auf ein Widget zeigt nur, was es nutzt.
        </span>
      }
    </section>

    @if (view(); as v) {
      <!-- two columns: widgets as the selection on the left (stays in view
           while scrolling), what they use on the right -->
      <div class="grid">
        <div class="col-12 lg:col-6 xl:col-5">
          <!-- Widgets -->
          <section class="inventory-widgets">
            <app-header
              icon="fas fa-table-cells-large"
              text="Widgets"
              [count]="widgetList().length" />
            <div class="flex flex-column gap-1 mt-2">
              @for (widget of widgetList(); track widget.id) {
                <button
                  type="button"
                  class="inventory-row flex align-items-center gap-3 p-2 border-round text-left w-full"
                  [class.selected]="selectedWidgetId() === widget.id"
                  [class.dimmed]="
                    selectedWidgetId() !== null &&
                    selectedWidgetId() !== widget.id
                  "
                  [attr.aria-pressed]="selectedWidgetId() === widget.id"
                  [pTooltip]="
                    selectedWidgetId() === widget.id
                      ? 'Filter aufheben'
                      : 'Nur anzeigen, was dieses Widget nutzt'
                  "
                  tooltipPosition="top"
                  (click)="toggleWidget(widget.id)">
                  <i
                    [class]="
                      widgetIcon(widget.type) + ' text-primary w-1rem'
                    "></i>
                  <span class="flex-1">
                    <span class="text-sm text-500 mr-2"
                      >#{{ widget.position }}</span
                    >
                    @if (widget.headline) {
                      <span class="font-medium">{{ widget.headline }}</span>
                    } @else {
                      <span class="font-italic text-600">{{
                        widgetLabel(widget)
                      }}</span>
                    }
                    <span class="text-sm text-500 ml-2">{{
                      widget.typeLabel
                    }}</span>
                  </span>
                  @if (widget.external) {
                    <p-tag value="Wegweiser" severity="secondary" />
                  }
                  @if (widget.issueCount > 0) {
                    <p-tag
                      [value]="
                        widget.issueCount +
                        ' Problem' +
                        (widget.issueCount === 1 ? '' : 'e')
                      "
                      severity="warning" />
                  } @else {
                    <i class="fas fa-check text-green-500"></i>
                  }
                  <i
                    class="inventory-row-action fas"
                    [class.fa-filter]="selectedWidgetId() !== widget.id"
                    [class.fa-xmark]="selectedWidgetId() === widget.id"></i>
                </button>
              } @empty {
                <div class="text-500 text-sm p-2">Keine Widgets.</div>
              }
            </div>
          </section>
        </div>

        <div class="col-12 lg:col-6 xl:col-7 flex flex-column gap-4">
          <!-- Indicators -->
          <section>
            <app-header
              icon="fas fa-ruler-vertical"
              text="Indikatoren"
              [count]="v.indicators.length" />
            <div class="flex flex-column gap-1 mt-2">
              @for (indicator of v.indicators; track indicator._id) {
                <div
                  class="inventory-row flex align-items-center gap-3 p-2 border-round">
                  <span class="flex-1">
                    <span class="font-medium">{{
                      indicatorFullName(indicator)
                    }}</span>
                    @if (indicator.categoryName) {
                      <span class="text-sm text-500 ml-2">
                        <i class="fas fa-folder mr-1"></i
                        >{{ indicator.categoryName }}
                      </span>
                    }
                  </span>
                  @if (indicator.hiddenPublicly) {
                    <p-tag
                      value="verborgener Arbeitsbereich"
                      severity="warning"
                      icon="fas fa-user-secret"
                      pTooltip="Liegt in einem verborgenen Arbeitsbereich und ist öffentlich nicht sichtbar" />
                  }
                  <p-tag
                    [value]="statusTag(indicator.status).label"
                    [severity]="statusTag(indicator.status).severity" />
                </div>
              } @empty {
                <div class="text-500 text-sm p-2">Keine Indikatoren.</div>
              }
            </div>
          </section>

          <!-- Spaces & years -->
          <section>
            <app-header
              icon="fas fa-map-location-dot"
              text="Räume & Jahre"
              [count]="v.spaces.length" />
            <div class="flex flex-column gap-2 mt-2">
              @for (space of v.spaces; track space._id) {
                <div class="inventory-row p-2 border-round">
                  <div class="flex align-items-center gap-2 mb-2">
                    <i
                      [class]="
                        space.kind === 'Standort'
                          ? 'fas fa-location-dot text-primary'
                          : 'fas fa-map text-primary'
                      "></i>
                    <span class="font-medium">{{ space.name }}</span>
                    @if (space.kind) {
                      <span class="text-sm text-500">{{ space.kind }}</span>
                    }
                    @if (space.status !== 'ok') {
                      <p-tag
                        [value]="statusTag(space.status).label"
                        [severity]="statusTag(space.status).severity" />
                    }
                  </div>
                  <div class="flex flex-wrap gap-2">
                    @for (year of space.years; track year._id) {
                      <p-tag
                        [value]="(year.year ?? '?') + ''"
                        [severity]="
                          year.status !== 'ok'
                            ? 'danger'
                            : year.published
                              ? 'info'
                              : 'warning'
                        "
                        [icon]="
                          year.status !== 'ok'
                            ? 'fas fa-trash'
                            : year.published
                              ? 'fas fa-earth-europe'
                              : 'fas fa-house-user'
                        "
                        [pTooltip]="
                          year.status !== 'ok'
                            ? statusTag(year.status).label
                            : year.published
                              ? 'öffentlich'
                              : 'intern – noch nicht veröffentlicht'
                        " />
                    } @empty {
                      <span class="text-sm text-500">
                        als Raum/Standort genutzt (ohne Jahr)
                      </span>
                    }
                  </div>
                </div>
              } @empty {
                <div class="text-500 text-sm p-2">Keine Räume.</div>
              }
            </div>
          </section>

          <!-- Data -->
          <section>
            <app-header
              icon="fas fa-database"
              text="Daten (Indikator × Jahr)"
              [count]="v.data.length" />
            <div class="flex flex-column gap-1 mt-2">
              @for (row of v.data; track row.dataLayerId + row.indicatorId) {
                <div
                  class="inventory-row flex align-items-center gap-3 p-2 border-round">
                  <span class="flex-1">
                    <span class="font-medium">{{ row.indicatorName }}</span>
                    <span class="text-sm text-500 ml-2">{{
                      row.yearLabel
                    }}</span>
                  </span>
                  <p-tag
                    [value]="dataStateTag(row.state).label"
                    [severity]="dataStateTag(row.state).severity" />
                </div>
              } @empty {
                <div class="text-500 text-sm p-2">
                  Keine Daten-Kombinationen.
                </div>
              }
            </div>
          </section>

          <!-- Other references -->
          <section>
            <app-header
              icon="fas fa-link"
              text="Weitere Verweise"
              [count]="v.references.length" />
            <div class="flex flex-column gap-1 mt-2">
              @for (
                reference of v.references;
                track reference.kind + reference._id
              ) {
                <div
                  class="inventory-row flex align-items-center gap-3 p-2 border-round">
                  <span class="text-sm text-500 w-8rem">{{
                    reference.kindLabel
                  }}</span>
                  <span class="flex-1 font-medium">{{ reference.name }}</span>
                  @if (reference.published === false) {
                    <p-tag
                      value="intern"
                      severity="warning"
                      icon="fas fa-house-user" />
                  }
                  <p-tag
                    [value]="statusTag(reference.status).label"
                    [severity]="statusTag(reference.status).severity" />
                </div>
              } @empty {
                <div class="text-500 text-sm p-2">Keine weiteren Verweise.</div>
              }
            </div>
          </section>
        </div>
      </div>
    }
  }
</div>
